import React, { useState, useCallback, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ResponsiveContainer,
} from 'recharts';
import api from '../services/api';
import { hojeBR } from '../utils/data';
import { useAuth } from '../contexts/AuthContext';
import ModalExportarLista from '../components/common/ModalExportarLista';
import s from './PedidosOrcamentos.module.css';
import InputDataFiltro from '../components/common/InputDataFiltro';

const fmtMoeda = v => Number(v||0).toLocaleString('pt-BR',{style:'currency',currency:'BRL'});
const fmtNum   = v => Number(v||0).toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtQtd   = v => Number(v||0).toLocaleString('pt-BR',{minimumFractionDigits:0,maximumFractionDigits:3});
const fmtData  = v => v ? new Date(String(v).slice(0,10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—';
const textoPeriodo = (ini, fim, rotulo = 'Período') => `${rotulo}: ${fmtData(ini)} a ${fmtData(fim)}`;
const hoje     = () => hojeBR();
const primeiroDiaMes = () => {
  const [ano, mes] = hoje().split('-');
  return `${ano}-${mes}-01`;
};

const ABAS = [
  { id:'resumo',    label:'Resumo' },
  { id:'vendas',    label:'Vendas por Período' },
  { id:'evolucao',  label:'Evolução de Vendas' },
  { id:'clientes',  label:'Vendas por Cliente' },
  { id:'abc',       label:'Curva ABC' },
  { id:'margem',    label:'Margem por Grupo' },
  { id:'produtos',  label:'Produtos' },
  { id:'aging',     label:'Aging (Pagar/Receber)' },
  { id:'comissoes', label:'Comissões' },
  { id:'contasPagar',   label:'Contas a Pagar' },
  { id:'contasReceber', label:'Contas a Receber' },
];

// Paleta categórica fixa (skill de dataviz — ordem fixa, nunca ciclada por
// dado) usada pra distinguir loja por loja nos gráficos "Consolidado".
const CORES_LOJA = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];

const AGRUPADORES_EVOLUCAO = [
  { id:'hora',       label:'Hora' },
  { id:'dia_mes',    label:'Dia do Mês' },
  { id:'dia_semana', label:'Dia da Semana' },
  { id:'mes',        label:'Mês' },
];

/* ── Estilos de tabela inline (pattern de ContasPagar) ── */
const thStyle  = { padding:'3px 6px', border:'1px solid #c0c8d8', background:'#dde3ef', textAlign:'center', fontSize:12 };
const tdStyle  = { padding:'3px 6px', border:'1px solid #ddd', fontSize:12 };
const trPar    = { background:'#fff' };
const trImpar  = { background:'#f5f8ff' };

/* Subabas dentro de uma aba (ex.: Comissões → Detalhado/Resumido) */
const subTabBtnStyle = (ativo) => ({
  padding:'5px 14px',
  border:'1px solid #1464c8',
  borderRadius:4,
  background: ativo ? '#1464c8' : '#fff',
  color: ativo ? '#fff' : '#1464c8',
  fontWeight: ativo ? 'bold' : 'normal',
  fontSize:12.5,
  cursor:'pointer',
});

const corAging = (faixa) => {
  if (faixa === 'A vencer')       return { background: '#e3f0ff', color: '#1464c8' };
  if (faixa === 'Vencido 1-30d')  return { background: '#fffbe6', color: '#b8860b' };
  if (faixa === 'Vencido 31-60d') return { background: '#fff3e0', color: '#e65100' };
  if (faixa === 'Vencido 61-90d') return { background: '#ffebee', color: '#c62828' };
  return { background: '#880000', color: '#fff' };
};

const badgeCurva = (curva) => {
  const base = { display:'inline-block', padding:'1px 8px', borderRadius:4, fontWeight:'bold', fontSize:12 };
  if (curva === 'A') return { ...base, background:'#28a745', color:'#fff' };
  if (curva === 'B') return { ...base, background:'#ffc107', color:'#333' };
  return { ...base, background:'#dc3545', color:'#fff' };
};

const corMargem = (pct) => {
  const v = parseFloat(pct||0);
  if (v > 30)  return { color:'#28a745', fontWeight:'bold' };
  if (v >= 15) return { color:'#e65100', fontWeight:'bold' };
  return { color:'#dc3545', fontWeight:'bold' };
};

export default function Relatorios() {
  const { usuario } = useAuth();
  const podeConsolidar = ['admin', 'gerente'].includes((usuario?.perfil || '').toLowerCase());
  const [aba, setAba] = useState('resumo');

  /* ── Exportar/Imprimir (qualquer relatório) — um modal só, reaproveitado
     por todas as abas: cada botão "Exportar/Imprimir" monta a config
     (título, colunas, dados) do relatório visível na hora e abre o modal. ── */
  const [modalExportar, setModalExportar] = useState(null); // { titulo, colunas, dados, nomeArquivo, filtros } | null
  // Filtros com que cada relatório foi GERADO (não os que estão na tela): quem
  // muda a data e imprime sem clicar em Gerar não pode sair com período errado.
  const [filtrosGerados, setFiltrosGerados] = useState({});
  const registrarFiltros = useCallback((relatorio, linhas) => {
    setFiltrosGerados(f => ({ ...f, [relatorio]: linhas.filter(Boolean) }));
  }, []);
  const BtnExportar = ({ onClick, disabled }) => (
    <button className={s.btnErp} onClick={onClick} disabled={disabled}
      title="Exportar para Excel/PDF ou imprimir este relatório">
      📤 Exportar/Imprimir
    </button>
  );

  /* ── Resumo ── */
  const [resumo, setResumo]         = useState(null);
  const [loadResumo, setLoadResumo] = useState(false);
  const [periodoRes, setPeriodoRes] = useState({ ini: primeiroDiaMes(), fim: hoje() });
  const [consolidar, setConsolidar] = useState(false);

  const carregarResumo = useCallback(async () => {
    setLoadResumo(true);
    try {
      const { data } = await api.get('/relatorios/resumo-geral', {
        params: { data_ini: periodoRes.ini, data_fim: periodoRes.fim, consolidar: podeConsolidar && consolidar ? 'true' : 'false' }
      });
      setResumo(data);
      registrarFiltros('resumo', [textoPeriodo(periodoRes.ini, periodoRes.fim), podeConsolidar && consolidar && 'Consolidado: todas as lojas']);
    } catch(e) { console.error(e); }
    finally { setLoadResumo(false); }
  }, [periodoRes, consolidar, podeConsolidar]);

  /* ── Vendas por período ── */
  const [subAbaVendas, setSubAbaVendas]   = useState('periodo'); // 'periodo' | 'vendedor' | 'caixa'
  const [vendasPeriodo, setVendasPeriodo] = useState([]);
  const [loadVendas,    setLoadVendas]    = useState(false);
  const [filtroVendas, setFiltroVendas]   = useState({ ini: primeiroDiaMes(), fim: hoje(), agrupamento: 'dia' });

  const carregarVendas = async () => {
    setLoadVendas(true);
    try {
      const { data } = await api.get('/relatorios/vendas-periodo', {
        params: { data_ini: filtroVendas.ini, data_fim: filtroVendas.fim, agrupamento: filtroVendas.agrupamento }
      });
      setVendasPeriodo(data);
      registrarFiltros('vendasPeriodo', [textoPeriodo(filtroVendas.ini, filtroVendas.fim), `Agrupamento: ${{ dia: 'Dia', semana: 'Semana', mes: 'Mês' }[filtroVendas.agrupamento]}`]);
    } catch(e) { console.error(e); }
    finally { setLoadVendas(false); }
  };

  /* ── Vendas por vendedor (mesma base, agrupada por vendedor) ── */
  const [vendasVendedor, setVendasVendedor] = useState([]);
  const [loadVendasVend, setLoadVendasVend] = useState(false);

  const carregarVendasVendedor = async () => {
    setLoadVendasVend(true);
    try {
      const { data } = await api.get('/relatorios/vendas-vendedor', {
        params: { data_ini: filtroVendas.ini, data_fim: filtroVendas.fim }
      });
      setVendasVendedor(data);
      registrarFiltros('vendasVendedor', [textoPeriodo(filtroVendas.ini, filtroVendas.fim)]);
    } catch(e) { console.error(e); setVendasVendedor([]); }
    finally { setLoadVendasVend(false); }
  };

  /* ── Vendas por caixa (mesma base, agrupada pela conta caixa do turno) ── */
  const [vendasCaixa, setVendasCaixa] = useState([]);
  const [loadVendasCaixa, setLoadVendasCaixa] = useState(false);

  const carregarVendasCaixa = async () => {
    setLoadVendasCaixa(true);
    try {
      const { data } = await api.get('/relatorios/vendas-caixa', {
        params: { data_ini: filtroVendas.ini, data_fim: filtroVendas.fim }
      });
      setVendasCaixa(data);
      registrarFiltros('vendasCaixa', [textoPeriodo(filtroVendas.ini, filtroVendas.fim)]);
    } catch(e) { console.error(e); setVendasCaixa([]); }
    finally { setLoadVendasCaixa(false); }
  };

  const gerarVendas = () => {
    if (subAbaVendas === 'vendedor') return carregarVendasVendedor();
    if (subAbaVendas === 'caixa') return carregarVendasCaixa();
    return carregarVendas();
  };
  const carregandoVendas = { periodo: loadVendas, vendedor: loadVendasVend, caixa: loadVendasCaixa }[subAbaVendas];

  /* ── Evolução de vendas ── */
  const [evolucao, setEvolucao]         = useState([]);
  const [loadEvolucao, setLoadEvolucao] = useState(false);
  const [filtroEvolucao, setFiltroEvolucao] = useState({ ini: primeiroDiaMes(), fim: hoje(), agrupador: 'hora' });

  const carregarEvolucao = async () => {
    setLoadEvolucao(true);
    try {
      const { data } = await api.get('/relatorios/evolucao-vendas', {
        params: {
          data_ini: `${filtroEvolucao.ini} 00:00:00`,
          data_fim: `${filtroEvolucao.fim} 23:59:59`,
          agrupador: filtroEvolucao.agrupador,
          consolidar: podeConsolidar && consolidar ? 'true' : 'false',
        }
      });
      setEvolucao(data.map(r => ({ ...r, total: Number(r.total || 0) })));
      registrarFiltros('evolucao', [textoPeriodo(filtroEvolucao.ini, filtroEvolucao.fim), `Agrupamento: ${AGRUPADORES_EVOLUCAO.find(a => a.id === filtroEvolucao.agrupador)?.label || filtroEvolucao.agrupador}`, podeConsolidar && consolidar && 'Consolidado: todas as lojas']);
    } catch(e) { console.error(e); setEvolucao([]); }
    finally { setLoadEvolucao(false); }
  };

  // Lojas distintas vindas do backend (só populado quando consolidar=true) —
  // define se o gráfico vira "uma barra por loja" em vez de somar tudo.
  const lojasEvolucao = React.useMemo(() => {
    const map = new Map();
    evolucao.forEach(r => { if (r.organizacao_id != null) map.set(r.organizacao_id, r.organizacao_nome); });
    return [...map.entries()].map(([id, nome]) => ({ id, nome })).sort((a, b) => a.id - b.id);
  }, [evolucao]);
  const multiLojaEvolucao = consolidar && lojasEvolucao.length > 1;

  // Pivota as linhas (uma por loja+período) pra uma linha por período com uma
  // coluna `loja_<id>` por loja — formato que o Recharts usa pra desenhar
  // barras agrupadas lado a lado.
  const evolucaoPivot = React.useMemo(() => {
    if (!multiLojaEvolucao) return evolucao;
    const porChave = new Map();
    evolucao.forEach(r => {
      if (!porChave.has(r.chave)) porChave.set(r.chave, { chave: r.chave, rotulo: r.rotulo });
      porChave.get(r.chave)[`loja_${r.organizacao_id}`] = r.total;
    });
    return [...porChave.values()].sort((a, b) => a.chave - b.chave);
  }, [evolucao, multiLojaEvolucao]);

  /* ── Vendas por cliente ── */
  const [vendasCliente, setVendasCliente]     = useState([]);
  const [loadVendasCli, setLoadVendasCli]     = useState(false);
  const [filtroVendasCli, setFiltroVendasCli] = useState({ ini: primeiroDiaMes(), fim: hoje() });

  const carregarVendasCliente = async () => {
    setLoadVendasCli(true);
    try {
      const { data } = await api.get('/relatorios/vendas-cliente', {
        params: {
          data_ini: `${filtroVendasCli.ini} 00:00:00`,
          data_fim: `${filtroVendasCli.fim} 23:59:59`,
        }
      });
      registrarFiltros('vendasCliente', [textoPeriodo(filtroVendasCli.ini, filtroVendasCli.fim)]);
      setVendasCliente(data.map(r => ({
        ...r, vendido: Number(r.vendido || 0), devolvido: Number(r.devolvido || 0), total: Number(r.total || 0),
      })));
    } catch(e) { console.error(e); setVendasCliente([]); }
    finally { setLoadVendasCli(false); }
  };

  /* ── Comissões ── */
  const [subAbaCom, setSubAbaCom]         = useState('detalhado'); // 'detalhado' | 'resumido'
  const [comissoes, setComissoes]         = useState(null); // payload completo do backend
  const [loadComissoes, setLoadComissoes] = useState(false);
  const [filtroCom, setFiltroCom]         = useState({ ini: primeiroDiaMes(), fim: hoje(), vendedor_id: '' });
  const [vendedores, setVendedores]       = useState([]);

  useEffect(() => {
    if (aba !== 'comissoes' || vendedores.length) return;
    api.get('/usuarios').then(r => setVendedores((r.data || []).filter(u => u.ativo))).catch(() => {});
  }, [aba]); // eslint-disable-line

  const carregarComissoes = async () => {
    setLoadComissoes(true);
    try {
      const { data } = await api.get('/relatorios/comissoes', {
        params: { de: filtroCom.ini, ate: filtroCom.fim, vendedor_id: filtroCom.vendedor_id || undefined }
      });
      setComissoes(data);
      registrarFiltros('comissoes', [textoPeriodo(filtroCom.ini, filtroCom.fim), `Vendedor: ${vendedores.find(u => String(u.id) === String(filtroCom.vendedor_id))?.nome || 'Todos'}`]);
    } catch(e) { console.error(e); setComissoes(null); }
    finally { setLoadComissoes(false); }
  };

  /* ── Contas a Pagar por Fornecedor ── */
  const [contasPagarLista, setContasPagarLista] = useState([]);
  const [loadCP, setLoadCP]           = useState(false);
  const [filtroCP, setFiltroCP]       = useState({ ini: primeiroDiaMes(), fim: hoje(), fornecedor: '' });

  const carregarContasPagar = async () => {
    setLoadCP(true);
    try {
      const { data } = await api.get('/relatorios/contas-pagar-fornecedor', {
        params: { venc_de: filtroCP.ini, venc_ate: filtroCP.fim, fornecedor: filtroCP.fornecedor || undefined }
      });
      setContasPagarLista(data);
      registrarFiltros('contasPagar', [textoPeriodo(filtroCP.ini, filtroCP.fim, 'Vencimento'), filtroCP.fornecedor && `Fornecedor: ${filtroCP.fornecedor}`]);
    } catch(e) { console.error(e); setContasPagarLista([]); }
    finally { setLoadCP(false); }
  };

  /* ── Contas a Receber por Período ── */
  const [contasReceberLista, setContasReceberLista] = useState([]);
  const [loadCR, setLoadCR]           = useState(false);
  const [filtroCR, setFiltroCR]       = useState({ ini: primeiroDiaMes(), fim: hoje(), cliente: '' });
  const [mostrarCartoesCR, setMostrarCartoesCR] = useState(false);

  const carregarContasReceber = async () => {
    setLoadCR(true);
    try {
      const { data } = await api.get('/relatorios/contas-receber-periodo', {
        params: {
          venc_de: filtroCR.ini, venc_ate: filtroCR.fim, cliente: filtroCR.cliente || undefined,
          mostrar_cartoes: mostrarCartoesCR ? 'true' : undefined,
        }
      });
      setContasReceberLista(data);
      registrarFiltros('contasReceber', [textoPeriodo(filtroCR.ini, filtroCR.fim, 'Vencimento'), filtroCR.cliente && `Cliente: ${filtroCR.cliente}`, mostrarCartoesCR && 'Inclui cartões']);
    } catch(e) { console.error(e); setContasReceberLista([]); }
    finally { setLoadCR(false); }
  };

  /* ── Curva ABC ── */
  const [abc,     setAbc]    = useState([]);
  const [loadAbc, setLoadAbc] = useState(false);
  const [filtroAbc, setFiltroAbc] = useState({ ini: primeiroDiaMes(), fim: hoje() });

  const carregarAbc = async () => {
    setLoadAbc(true);
    try {
      const { data } = await api.get('/relatorios/curva-abc', {
        params: { data_ini: filtroAbc.ini, data_fim: filtroAbc.fim, limite: 200 }
      });
      setAbc(data);
      registrarFiltros('abc', [textoPeriodo(filtroAbc.ini, filtroAbc.fim)]);
    } catch(e) { console.error(e); }
    finally { setLoadAbc(false); }
  };

  /* ── Margem por grupo ── */
  const [margem,     setMargem]    = useState([]);
  const [loadMargem, setLoadMargem] = useState(false);
  const [filtroMargem, setFiltroMargem] = useState({ ini: primeiroDiaMes(), fim: hoje() });

  const carregarMargem = async () => {
    setLoadMargem(true);
    try {
      const { data } = await api.get('/relatorios/margem-grupos', {
        params: { data_ini: filtroMargem.ini, data_fim: filtroMargem.fim }
      });
      setMargem(data);
      registrarFiltros('margem', [textoPeriodo(filtroMargem.ini, filtroMargem.fim)]);
    } catch(e) { console.error(e); }
    finally { setLoadMargem(false); }
  };

  /* ── Produtos → Valores do Estoque ── */
  const [subAbaProd, setSubAbaProd]     = useState('valoresEstoque');
  const [valEstoque, setValEstoque]     = useState(null); // { itens, total_custo, total_venda }
  const [loadValEst, setLoadValEst]     = useState(false);
  const [filtroValEst, setFiltroValEst] = useState({ incluir_inativos: false, estoque_positivo: false, custo_positivo: false, venda_positivo: false });

  const carregarValoresEstoque = async () => {
    setLoadValEst(true);
    try {
      const { data } = await api.get('/relatorios/valores-estoque', {
        params: {
          incluir_inativos: filtroValEst.incluir_inativos ? 'true' : undefined,
          estoque_positivo: filtroValEst.estoque_positivo ? 'true' : undefined,
          custo_positivo:   filtroValEst.custo_positivo ? 'true' : undefined,
          venda_positivo:   filtroValEst.venda_positivo ? 'true' : undefined,
        }
      });
      setValEstoque(data);
      registrarFiltros('valEstoque', [
        filtroValEst.incluir_inativos && 'Inclui produtos inativos',
        filtroValEst.estoque_positivo && 'Somente estoque positivo',
        filtroValEst.custo_positivo && 'Somente custo positivo',
        filtroValEst.venda_positivo && 'Somente venda positiva',
      ]);
    } catch(e) { console.error(e); setValEstoque(null); }
    finally { setLoadValEst(false); }
  };

  /* ── Aging ── */
  const [agingPagar,   setAgingPagar]   = useState([]);
  const [agingReceber, setAgingReceber] = useState([]);
  const [loadAging,    setLoadAging]    = useState(false);

  const carregarAgingPagar = async () => {
    try {
      const { data } = await api.get('/relatorios/aging-pagar');
      setAgingPagar(data);
    } catch(e) { console.error(e); }
  };

  const carregarAgingReceber = async () => {
    try {
      const { data } = await api.get('/relatorios/aging-receber');
      setAgingReceber(data);
    } catch(e) { console.error(e); }
  };

  /* ── Auto-load ao mudar aba ── */
  useEffect(() => {
    if (aba === 'resumo') carregarResumo();
    if (aba === 'aging') {
      setLoadAging(true);
      Promise.all([carregarAgingPagar(), carregarAgingReceber()])
        .finally(() => setLoadAging(false));
    }
  }, [aba]); // eslint-disable-line

  /* totais para rodapé de vendas */
  const totaisVendas = vendasPeriodo.reduce((acc, r) => ({
    qtd_vendas:     acc.qtd_vendas     + Number(r.qtd_vendas||0),
    total_bruto:    acc.total_bruto    + Number(r.total_bruto||0),
    total_desconto: acc.total_desconto + Number(r.total_desconto||0),
    total_taxa_entrega: acc.total_taxa_entrega + Number(r.total_taxa_entrega||0),
    total_liquido:  acc.total_liquido  + Number(r.total_liquido||0),
  }), { qtd_vendas:0, total_bruto:0, total_desconto:0, total_taxa_entrega:0, total_liquido:0 });
  // Coluna "Taxa entrega" só aparece se houve taxa no período.
  const temTaxaEntrega = totaisVendas.total_taxa_entrega > 0;

  const totaisVendasCaixa = vendasCaixa.reduce((acc, r) => ({
    qtd_vendas: acc.qtd_vendas + Number(r.qtd_vendas || 0),
    valor_liquido: acc.valor_liquido + Number(r.valor_liquido || 0),
  }), { qtd_vendas: 0, valor_liquido: 0 });

  const totaisVendasVend = vendasVendedor.reduce((acc, r) => ({
    qtd_pedidos:   acc.qtd_pedidos   + Number(r.qtd_pedidos||0),
    valor_liquido: acc.valor_liquido + Number(r.valor_liquido||0),
  }), { qtd_pedidos:0, valor_liquido:0 });

  const totalContasPagar   = contasPagarLista.reduce((a, r) => a + Number(r.valor||0), 0);
  const totalContasReceber = contasReceberLista.reduce((a, r) => a + Number(r.valor||0), 0);

  return (
    <div className={s.erpWrap}>
      {/* Barra de título */}
      <div className={s.titleBar}>
        <span>📈</span>
        <span>Relatórios Gerenciais</span>
      </div>

      {/* Barra de abas — rolagem horizontal quando não cabem todas na tela (celular) */}
      <div style={{ display:'flex', gap:0, background:'#e0e0e0', borderBottom:'2px solid #1464c8', flexShrink:0, overflowX:'auto', whiteSpace:'nowrap' }}>
        {ABAS.map(a => (
          <button
            key={a.id}
            onClick={() => setAba(a.id)}
            style={{
              padding:'6px 18px',
              border:'none',
              borderRight:'1px solid #bbb',
              background: aba === a.id ? '#fff' : '#e0e0e0',
              fontWeight: aba === a.id ? 'bold' : 'normal',
              color: aba === a.id ? '#0a3a8c' : '#333',
              cursor:'pointer',
              fontSize:13,
              flexShrink:0,
              borderBottom: aba === a.id ? '2px solid #fff' : 'none',
              marginBottom: aba === a.id ? -2 : 0,
            }}
          >
            {a.label}
          </button>
        ))}
      </div>

      {/* Área de conteúdo */}
      <div style={{ flex:1, overflow:'auto', padding:16, background:'#fff' }}>

        {/* ══════════ ABA RESUMO ══════════ */}
        {aba === 'resumo' && (
          <div>
            <div className={s.headerRow} style={{ marginBottom:16 }}>
              <label className={s.headerLabel}>De:</label>
              <InputDataFiltro
                className={s.inputErp}
                style={{ width:130 }}
                value={periodoRes.ini}
                onChange={e => setPeriodoRes(p => ({...p, ini: e.target.value}))}
              />
              <label className={s.headerLabel}>Até:</label>
              <InputDataFiltro
                className={s.inputErp}
                style={{ width:130 }}
                value={periodoRes.fim}
                onChange={e => setPeriodoRes(p => ({...p, fim: e.target.value}))}
              />
              <button
                className={`${s.btnErp} ${s.btnErpPrimario}`}
                onClick={carregarResumo}
                disabled={loadResumo}
              >
                {loadResumo ? 'Carregando...' : 'Atualizar'}
              </button>
              {podeConsolidar && (
                <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer', marginLeft:8 }}
                  title="Soma o resumo de TODAS as organizações cadastradas neste banco, não só a atual">
                  <input type="checkbox" checked={consolidar}
                    onChange={e => setConsolidar(e.target.checked)} />
                  Consolidar todas as lojas
                </label>
              )}
              <BtnExportar disabled={!resumo} onClick={() => setModalExportar({
                titulo: 'Resumo Geral', nomeArquivo: 'resumo-geral', filtros: filtrosGerados.resumo,
                dados: [
                  { indicador: 'Vendas',     valor: resumo?.vendas?.total,   qtd: resumo?.vendas?.qtd,   obs: 'pedidos' },
                  { indicador: 'Compras NF', valor: resumo?.compras?.total,  qtd: resumo?.compras?.qtd,  obs: 'NFs confirmadas' },
                  { indicador: 'Recebido',   valor: resumo?.recebido?.total, qtd: null, obs: 'contas a receber' },
                  { indicador: 'Pago',       valor: resumo?.pago?.total,     qtd: null, obs: 'contas a pagar' },
                ],
                colunas: [
                  { key: 'indicador', label: 'Indicador' },
                  { key: 'valor', label: 'Valor', render: d => fmtMoeda(d.valor) },
                  { key: 'qtd', label: 'Quantidade' },
                  { key: 'obs', label: 'Observação' },
                ],
              })} />
            </div>

            {loadResumo && <p style={{ color:'#666' }}>Carregando dados...</p>}

            {resumo?.consolidado && (
              <div style={{ display:'inline-block', background:'#eef2ff', color:'#3730a3', fontSize:12,
                fontWeight:'bold', padding:'4px 10px', borderRadius:6, marginBottom:12 }}>
                🏬 Consolidado — soma de todas as lojas cadastradas
              </div>
            )}

            {resumo && (
              <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                <div style={{ background:'#1464c8', borderRadius:8, padding:20, minWidth:180, textAlign:'center', color:'#fff' }}>
                  <div style={{ fontSize:24, fontWeight:'bold' }}>{fmtMoeda(resumo.vendas?.total)}</div>
                  <div style={{ fontSize:13, marginTop:4 }}>💰 Vendas</div>
                  <div style={{ fontSize:12, marginTop:2, opacity:0.85 }}>{resumo.vendas?.qtd} pedidos</div>
                </div>
                <div style={{ background:'#e65100', borderRadius:8, padding:20, minWidth:180, textAlign:'center', color:'#fff' }}>
                  <div style={{ fontSize:24, fontWeight:'bold' }}>{fmtMoeda(resumo.compras?.total)}</div>
                  <div style={{ fontSize:13, marginTop:4 }}>📦 Compras NF</div>
                  <div style={{ fontSize:12, marginTop:2, opacity:0.85 }}>{resumo.compras?.qtd} NFs confirmadas</div>
                </div>
                <div style={{ background:'#28a745', borderRadius:8, padding:20, minWidth:180, textAlign:'center', color:'#fff' }}>
                  <div style={{ fontSize:24, fontWeight:'bold' }}>{fmtMoeda(resumo.recebido?.total)}</div>
                  <div style={{ fontSize:13, marginTop:4 }}>✅ Recebido</div>
                  <div style={{ fontSize:12, marginTop:2, opacity:0.85 }}>contas a receber</div>
                </div>
                <div style={{ background:'#dc3545', borderRadius:8, padding:20, minWidth:180, textAlign:'center', color:'#fff' }}>
                  <div style={{ fontSize:24, fontWeight:'bold' }}>{fmtMoeda(resumo.pago?.total)}</div>
                  <div style={{ fontSize:13, marginTop:4 }}>💳 Pago</div>
                  <div style={{ fontSize:12, marginTop:2, opacity:0.85 }}>contas a pagar</div>
                </div>
              </div>
            )}

            {!loadResumo && !resumo && (
              <p style={{ color:'#888' }}>Clique em "Atualizar" para carregar o resumo do período.</p>
            )}
          </div>
        )}

        {/* ══════════ ABA VENDAS POR PERÍODO ══════════ */}
        {aba === 'vendas' && (
          <div>
            <div style={{ display:'flex', gap:6, marginBottom:12 }}>
              <button
                onClick={() => setSubAbaVendas('periodo')}
                style={subTabBtnStyle(subAbaVendas === 'periodo')}
              >Por Período</button>
              <button
                onClick={() => setSubAbaVendas('vendedor')}
                style={subTabBtnStyle(subAbaVendas === 'vendedor')}
              >Por Vendedor</button>
              <button
                onClick={() => setSubAbaVendas('caixa')}
                style={subTabBtnStyle(subAbaVendas === 'caixa')}
              >Por Caixa</button>
            </div>

            <div className={s.headerRow} style={{ marginBottom:12 }}>
              <label className={s.headerLabel}>De:</label>
              <InputDataFiltro
                className={s.inputErp}
                style={{ width:130 }}
                value={filtroVendas.ini}
                onChange={e => setFiltroVendas(f => ({...f, ini: e.target.value}))}
              />
              <label className={s.headerLabel}>Até:</label>
              <InputDataFiltro
                className={s.inputErp}
                style={{ width:130 }}
                value={filtroVendas.fim}
                onChange={e => setFiltroVendas(f => ({...f, fim: e.target.value}))}
              />
              {subAbaVendas === 'periodo' && (
                <>
                  <label className={s.headerLabel}>Agrupamento:</label>
                  <select
                    className={s.selectErp}
                    value={filtroVendas.agrupamento}
                    onChange={e => setFiltroVendas(f => ({...f, agrupamento: e.target.value}))}
                  >
                    <option value="dia">Dia</option>
                    <option value="semana">Semana</option>
                    <option value="mes">Mês</option>
                  </select>
                </>
              )}
              <button
                className={`${s.btnErp} ${s.btnErpPrimario}`}
                onClick={gerarVendas}
                disabled={carregandoVendas}
              >
                {carregandoVendas ? 'Carregando...' : 'Gerar'}
              </button>
              {subAbaVendas === 'caixa' ? (
                <BtnExportar disabled={!vendasCaixa.length} onClick={() => setModalExportar({
                  titulo: 'Vendas por Caixa', nomeArquivo: 'vendas-por-caixa', filtros: filtrosGerados.vendasCaixa,
                  dados: vendasCaixa,
                  colunas: [
                    { key: 'caixa_id', label: 'Cód. Caixa' },
                    { key: 'caixa_nome', label: 'Caixa' },
                    { key: 'qtd_vendas', label: 'Qtd Vendas' },
                    { key: 'valor_liquido', label: 'Valor Líquido', render: r => fmtMoeda(r.valor_liquido) },
                  ],
                })} />
              ) : subAbaVendas === 'periodo' ? (
                <BtnExportar disabled={!vendasPeriodo.length} onClick={() => setModalExportar({
                  titulo: 'Vendas por Período', nomeArquivo: 'vendas-por-periodo', filtros: filtrosGerados.vendasPeriodo,
                  dados: vendasPeriodo,
                  colunas: [
                    { key: 'periodo', label: 'Período', render: r => fmtData(r.periodo) },
                    { key: 'qtd_vendas', label: 'Qtd Vendas' },
                    { key: 'total_bruto', label: 'Total Bruto', render: r => fmtMoeda(r.total_bruto) },
                    { key: 'total_desconto', label: 'Desconto', render: r => fmtMoeda(r.total_desconto) },
                    ...(temTaxaEntrega ? [{ key: 'total_taxa_entrega', label: 'Taxa Entrega', render: r => fmtMoeda(r.total_taxa_entrega) }] : []),
                    { key: 'total_liquido', label: 'Total Líquido', render: r => fmtMoeda(r.total_liquido) },
                  ],
                })} />
              ) : (
                <BtnExportar disabled={!vendasVendedor.length} onClick={() => setModalExportar({
                  titulo: 'Vendas por Vendedor', nomeArquivo: 'vendas-por-vendedor', filtros: filtrosGerados.vendasVendedor,
                  dados: vendasVendedor,
                  colunas: [
                    { key: 'vendedor_id', label: 'Cód. Vendedor' },
                    { key: 'vendedor_nome', label: 'Nome Vendedor' },
                    { key: 'qtd_pedidos', label: 'Qtd Pedidos' },
                    { key: 'valor_liquido', label: 'Valor Líquido', render: r => fmtMoeda(r.valor_liquido) },
                  ],
                })} />
              )}
            </div>

            {subAbaVendas === 'periodo' && (
              <>
                {vendasPeriodo.length === 0 && !loadVendas && (
                  <p style={{ color:'#888' }}>Selecione o período e clique em "Gerar".</p>
                )}

                {vendasPeriodo.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead style={{ position:'sticky', top:0, zIndex:1 }}>
                      <tr>
                        <th style={{ ...thStyle, textAlign:'left' }}>Período</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Qtd Vendas</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Total Bruto</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Desconto</th>
                        {temTaxaEntrega && <th style={{ ...thStyle, textAlign:'right' }}>Taxa Entrega</th>}
                        <th style={{ ...thStyle, textAlign:'right' }}>Total Líquido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendasPeriodo.map((r, i) => (
                        <tr key={i} style={i % 2 === 0 ? trPar : trImpar}>
                          <td style={tdStyle}>{fmtData(r.periodo)}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{r.qtd_vendas}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.total_bruto)}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.total_desconto)}</td>
                          {temTaxaEntrega && <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.total_taxa_entrega)}</td>}
                          <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.total_liquido)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#e8f0ff', fontWeight:'bold' }}>
                        <td style={tdStyle}>TOTAL</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{totaisVendas.qtd_vendas}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(totaisVendas.total_bruto)}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(totaisVendas.total_desconto)}</td>
                        {temTaxaEntrega && <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(totaisVendas.total_taxa_entrega)}</td>}
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(totaisVendas.total_liquido)}</td>
                      </tr>
                    </tfoot>
                  </table>
                  </div>
                )}
              </>
            )}

            {subAbaVendas === 'caixa' && (
              <>
                {vendasCaixa.length === 0 && !loadVendasCaixa && (
                  <p style={{ color:'#888' }}>Selecione o período e clique em "Gerar".</p>
                )}

                {vendasCaixa.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead style={{ position:'sticky', top:0, zIndex:1 }}>
                      <tr>
                        <th style={thStyle}>Cód. Caixa</th>
                        <th style={{ ...thStyle, textAlign:'left' }}>Caixa</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Qtd Vendas</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Valor Líquido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendasCaixa.map((r, i) => (
                        <tr key={i} style={i % 2 === 0 ? trPar : trImpar}>
                          <td style={{ ...tdStyle, textAlign:'center' }}>{r.caixa_id ?? '—'}</td>
                          <td style={tdStyle}>{r.caixa_nome}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{r.qtd_vendas}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.valor_liquido)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#e8f0ff', fontWeight:'bold' }}>
                        <td style={tdStyle} colSpan={2}>TOTAL</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{totaisVendasCaixa.qtd_vendas}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(totaisVendasCaixa.valor_liquido)}</td>
                      </tr>
                    </tfoot>
                  </table>
                  </div>
                )}
              </>
            )}

            {subAbaVendas === 'vendedor' && (
              <>
                {vendasVendedor.length === 0 && !loadVendasVend && (
                  <p style={{ color:'#888' }}>Selecione o período e clique em "Gerar".</p>
                )}

                {vendasVendedor.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead style={{ position:'sticky', top:0, zIndex:1 }}>
                      <tr>
                        <th style={thStyle}>Cód. Vendedor</th>
                        <th style={{ ...thStyle, textAlign:'left' }}>Nome Vendedor</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Qtd Pedidos</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Valor Líquido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendasVendedor.map((r, i) => (
                        <tr key={i} style={i % 2 === 0 ? trPar : trImpar}>
                          <td style={{ ...tdStyle, textAlign:'center' }}>{r.vendedor_id ?? '—'}</td>
                          <td style={tdStyle}>{r.vendedor_nome}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{r.qtd_pedidos}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.valor_liquido)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#e8f0ff', fontWeight:'bold' }}>
                        <td style={tdStyle} colSpan={2}>TOTAL</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{totaisVendasVend.qtd_pedidos}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(totaisVendasVend.valor_liquido)}</td>
                      </tr>
                    </tfoot>
                  </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══════════ ABA EVOLUÇÃO DE VENDAS ══════════ */}
        {aba === 'evolucao' && (
          <div>
            <div className={s.headerRow} style={{ marginBottom:6 }}>
              <label className={s.headerLabel}>De:</label>
              <InputDataFiltro
                className={s.inputErp}
                style={{ width:130 }}
                value={filtroEvolucao.ini}
                onChange={e => setFiltroEvolucao(f => ({...f, ini: e.target.value}))}
              />
              <label className={s.headerLabel}>Até:</label>
              <InputDataFiltro
                className={s.inputErp}
                style={{ width:130 }}
                value={filtroEvolucao.fim}
                onChange={e => setFiltroEvolucao(f => ({...f, fim: e.target.value}))}
              />
              <label className={s.headerLabel}>Agrupador:</label>
              <select
                className={s.selectErp}
                value={filtroEvolucao.agrupador}
                onChange={e => setFiltroEvolucao(f => ({...f, agrupador: e.target.value}))}
              >
                {AGRUPADORES_EVOLUCAO.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
              <button
                className={`${s.btnErp} ${s.btnErpPrimario}`}
                onClick={carregarEvolucao}
                disabled={loadEvolucao}
              >
                {loadEvolucao ? 'Carregando...' : 'Gerar'}
              </button>
              {podeConsolidar && (
                <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer', marginLeft:8 }}
                  title="Mostra uma barra por loja lado a lado, em vez de somar tudo numa barra só">
                  <input type="checkbox" checked={consolidar}
                    onChange={e => setConsolidar(e.target.checked)} />
                  Consolidar todas as lojas
                </label>
              )}
              <BtnExportar disabled={!evolucao.length} onClick={() => setModalExportar({
                titulo: 'Evolução de Vendas', nomeArquivo: 'evolucao-vendas', filtros: filtrosGerados.evolucao,
                dados: evolucao,
                colunas: [
                  { key: 'rotulo', label: AGRUPADORES_EVOLUCAO.find(a => a.id === filtroEvolucao.agrupador)?.label || 'Período' },
                  { key: 'total', label: 'Total', render: r => fmtMoeda(r.total) },
                ],
              })} />
            </div>
            <p style={{ fontSize:11.5, color:'#888', margin:'0 0 14px' }}>
              * O relatório considera produtos e serviços · Os valores consideram vendas − devoluções
            </p>

            {evolucao.length === 0 && !loadEvolucao && (
              <p style={{ color:'#888' }}>Selecione o período e o agrupador, e clique em "Gerar".</p>
            )}

            {evolucao.length > 0 && (
              <>
                <h3 style={{ margin:'0 0 12px', color:'#0a3a8c', fontSize:14 }}>
                  Vendas por {AGRUPADORES_EVOLUCAO.find(a => a.id === filtroEvolucao.agrupador)?.label}
                  {multiLojaEvolucao && <span style={{ fontWeight:'normal', fontSize:12.5, color:'#666' }}> — por loja</span>}
                </h3>
                <ResponsiveContainer width="100%" height={360}>
                  {multiLojaEvolucao ? (
                    <BarChart data={evolucaoPivot} margin={{ top: 24, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="rotulo" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={v => fmtMoeda(v)} tick={{ fontSize: 11 }} width={90} />
                      <Tooltip formatter={v => fmtMoeda(v)} labelFormatter={l => `${AGRUPADORES_EVOLUCAO.find(a => a.id === filtroEvolucao.agrupador)?.label}: ${l}`} />
                      <Legend wrapperStyle={{ fontSize: 12.5 }} />
                      {lojasEvolucao.map((loja, i) => (
                        <Bar key={loja.id} dataKey={`loja_${loja.id}`} name={loja.nome}
                          fill={CORES_LOJA[i % CORES_LOJA.length]} radius={[4, 4, 0, 0]} />
                      ))}
                    </BarChart>
                  ) : (
                    <BarChart data={evolucao} margin={{ top: 24, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="rotulo" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={v => fmtMoeda(v)} tick={{ fontSize: 11 }} width={90} />
                      <Tooltip formatter={v => fmtMoeda(v)} labelFormatter={l => `${AGRUPADORES_EVOLUCAO.find(a => a.id === filtroEvolucao.agrupador)?.label}: ${l}`} />
                      <Bar dataKey="total" name="Total" fill="#1464c8" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="total" position="top" formatter={v => fmtMoeda(v)} style={{ fontSize: 11, fill: '#333' }} />
                      </Bar>
                    </BarChart>
                  )}
                </ResponsiveContainer>
                <div style={{ textAlign:'right', marginTop:12, fontSize:15 }}>
                  <strong style={{ color:'#1464c8' }}>
                    {fmtMoeda(evolucao.reduce((acc, r) => acc + r.total, 0))}
                  </strong>
                  <div style={{ fontSize:11.5, color:'#888' }}>Total das vendas filtradas</div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ══════════ ABA VENDAS POR CLIENTE ══════════ */}
        {aba === 'clientes' && (
          <div>
            <div className={s.headerRow} style={{ marginBottom:6 }}>
              <label className={s.headerLabel}>De:</label>
              <InputDataFiltro
                className={s.inputErp}
                style={{ width:130 }}
                value={filtroVendasCli.ini}
                onChange={e => setFiltroVendasCli(f => ({...f, ini: e.target.value}))}
              />
              <label className={s.headerLabel}>Até:</label>
              <InputDataFiltro
                className={s.inputErp}
                style={{ width:130 }}
                value={filtroVendasCli.fim}
                onChange={e => setFiltroVendasCli(f => ({...f, fim: e.target.value}))}
              />
              <button
                className={`${s.btnErp} ${s.btnErpPrimario}`}
                onClick={carregarVendasCliente}
                disabled={loadVendasCli}
              >
                {loadVendasCli ? 'Carregando...' : 'Gerar'}
              </button>
              <BtnExportar disabled={!vendasCliente.length} onClick={() => setModalExportar({
                titulo: 'Vendas por Cliente', nomeArquivo: 'vendas-por-cliente', filtros: filtrosGerados.vendasCliente,
                dados: vendasCliente,
                colunas: [
                  { key: 'codigo', label: 'Código' },
                  { key: 'nome', label: 'Nome' },
                  { key: 'vendido', label: 'Vendido R$', render: r => fmtNum(r.vendido) },
                  { key: 'devolvido', label: 'Devolvido R$', render: r => fmtNum(r.devolvido) },
                  { key: 'total', label: 'Total R$', render: r => fmtNum(r.total) },
                ],
              })} />
            </div>
            <p style={{ fontSize:11.5, color:'#888', margin:'0 0 14px' }}>
              * O relatório considera produtos e serviços · Os valores já consideram descontos e acréscimos
            </p>

            {vendasCliente.length === 0 && !loadVendasCli && (
              <p style={{ color:'#888' }}>Selecione o período e clique em "Gerar".</p>
            )}

            {vendasCliente.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={{ position:'sticky', top:0, zIndex:1 }}>
                  <tr>
                    <th style={{ ...thStyle, textAlign:'left' }}>Código</th>
                    <th style={{ ...thStyle, textAlign:'left' }}>Nome</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Vendido R$</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Devolvido R$</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Total R$</th>
                  </tr>
                </thead>
                <tbody>
                  {vendasCliente.map((r, i) => (
                    <tr key={i} style={i % 2 === 0 ? trPar : trImpar}>
                      <td style={tdStyle}>{r.codigo ?? '—'}</td>
                      <td style={tdStyle}>{r.nome || '—'}</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(r.vendido)}</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(r.devolvido)}</td>
                      <td style={{ ...tdStyle, textAlign:'right', fontWeight:600 }}>{fmtNum(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background:'#e8f0ff', fontWeight:'bold' }}>
                    <td style={tdStyle} colSpan={2}>Total:</td>
                    <td style={{ ...tdStyle, textAlign:'right' }}>
                      {fmtNum(vendasCliente.reduce((a, r) => a + r.vendido, 0))}
                    </td>
                    <td style={{ ...tdStyle, textAlign:'right' }}>
                      {fmtNum(vendasCliente.reduce((a, r) => a + r.devolvido, 0))}
                    </td>
                    <td style={{ ...tdStyle, textAlign:'right' }}>
                      {fmtNum(vendasCliente.reduce((a, r) => a + r.total, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
              </div>
            )}
          </div>
        )}

        {/* ══════════ ABA CURVA ABC ══════════ */}
        {aba === 'abc' && (
          <div>
            <div className={s.headerRow} style={{ marginBottom:12 }}>
              <label className={s.headerLabel}>De:</label>
              <InputDataFiltro
                className={s.inputErp}
                style={{ width:130 }}
                value={filtroAbc.ini}
                onChange={e => setFiltroAbc(f => ({...f, ini: e.target.value}))}
              />
              <label className={s.headerLabel}>Até:</label>
              <InputDataFiltro
                className={s.inputErp}
                style={{ width:130 }}
                value={filtroAbc.fim}
                onChange={e => setFiltroAbc(f => ({...f, fim: e.target.value}))}
              />
              <button
                className={`${s.btnErp} ${s.btnErpPrimario}`}
                onClick={carregarAbc}
                disabled={loadAbc}
              >
                {loadAbc ? 'Carregando...' : 'Gerar'}
              </button>
              <BtnExportar disabled={!abc.length} onClick={() => setModalExportar({
                titulo: 'Curva ABC', nomeArquivo: 'curva-abc', filtros: filtrosGerados.abc,
                dados: abc,
                colunas: [
                  { key: 'curva', label: 'Curva' },
                  { key: 'codigo_interno', label: 'Código' },
                  { key: 'nome', label: 'Produto' },
                  { key: 'grupo', label: 'Grupo' },
                  { key: 'qtd_total', label: 'Qtd Vendida', render: r => fmtNum(r.qtd_total) },
                  { key: 'faturamento', label: 'Faturamento', render: r => fmtMoeda(r.faturamento) },
                  { key: 'custo_total', label: 'Custo', render: r => fmtMoeda(r.custo_total) },
                  { key: 'margem_pct', label: 'Margem %', render: r => fmtNum(r.margem_pct) + '%' },
                  { key: 'perc_faturamento', label: '% Fat.', render: r => fmtNum(r.perc_faturamento) + '%' },
                  { key: 'perc_acumulado', label: '% Acum.', render: r => fmtNum(r.perc_acumulado) + '%' },
                ],
              })} />
            </div>

            {abc.length === 0 && !loadAbc && (
              <p style={{ color:'#888' }}>Selecione o período e clique em "Gerar".</p>
            )}

            {abc.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={{ position:'sticky', top:0, zIndex:1 }}>
                  <tr>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Curva</th>
                    <th style={{ ...thStyle, textAlign:'left' }}>Código</th>
                    <th style={{ ...thStyle, textAlign:'left' }}>Produto</th>
                    <th style={{ ...thStyle, textAlign:'left' }}>Grupo</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Qtd Vendida</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Faturamento</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Custo</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Margem %</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>% Fat.</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>% Acum.</th>
                  </tr>
                </thead>
                <tbody>
                  {abc.map((r, i) => (
                    <tr key={i} style={i % 2 === 0 ? trPar : trImpar}>
                      <td style={{ ...tdStyle, textAlign:'center' }}>{i+1}</td>
                      <td style={{ ...tdStyle, textAlign:'center' }}>
                        <span style={badgeCurva(r.curva)}>{r.curva}</span>
                      </td>
                      <td style={tdStyle}>{r.codigo_interno}</td>
                      <td style={tdStyle}>{r.nome}</td>
                      <td style={tdStyle}>{r.grupo || '—'}</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(r.qtd_total)}</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.faturamento)}</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.custo_total)}</td>
                      <td style={{ ...tdStyle, textAlign:'right', ...corMargem(r.margem_pct) }}>{fmtNum(r.margem_pct)}%</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(r.perc_faturamento)}%</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(r.perc_acumulado)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}

        {/* ══════════ ABA MARGEM POR GRUPO ══════════ */}
        {aba === 'margem' && (
          <div>
            <div className={s.headerRow} style={{ marginBottom:12 }}>
              <label className={s.headerLabel}>De:</label>
              <InputDataFiltro
                className={s.inputErp}
                style={{ width:130 }}
                value={filtroMargem.ini}
                onChange={e => setFiltroMargem(f => ({...f, ini: e.target.value}))}
              />
              <label className={s.headerLabel}>Até:</label>
              <InputDataFiltro
                className={s.inputErp}
                style={{ width:130 }}
                value={filtroMargem.fim}
                onChange={e => setFiltroMargem(f => ({...f, fim: e.target.value}))}
              />
              <button
                className={`${s.btnErp} ${s.btnErpPrimario}`}
                onClick={carregarMargem}
                disabled={loadMargem}
              >
                {loadMargem ? 'Carregando...' : 'Gerar'}
              </button>
              <BtnExportar disabled={!margem.length} onClick={() => setModalExportar({
                titulo: 'Margem por Grupo', nomeArquivo: 'margem-por-grupo', filtros: filtrosGerados.margem,
                dados: margem,
                colunas: [
                  { key: 'grupo', label: 'Grupo' },
                  { key: 'qtd_produtos', label: 'Qtd Produtos' },
                  { key: 'qtd_vendida', label: 'Qtd Vendida', render: r => fmtNum(r.qtd_vendida) },
                  { key: 'faturamento', label: 'Faturamento', render: r => fmtMoeda(r.faturamento) },
                  { key: 'custo', label: 'Custo', render: r => fmtMoeda(r.custo) },
                  { key: 'margem_pct', label: 'Margem %', render: r => fmtNum(r.margem_pct) + '%' },
                ],
              })} />
            </div>

            {margem.length === 0 && !loadMargem && (
              <p style={{ color:'#888' }}>Selecione o período e clique em "Gerar".</p>
            )}

            {margem.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={{ position:'sticky', top:0, zIndex:1 }}>
                  <tr>
                    <th style={{ ...thStyle, textAlign:'left' }}>Grupo</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Qtd Produtos</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Qtd Vendida</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Faturamento</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Custo</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Margem %</th>
                  </tr>
                </thead>
                <tbody>
                  {margem.map((r, i) => (
                    <tr key={i} style={i % 2 === 0 ? trPar : trImpar}>
                      <td style={tdStyle}>{r.grupo}</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{r.qtd_produtos}</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(r.qtd_vendida)}</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.faturamento)}</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.custo)}</td>
                      <td style={{ ...tdStyle, textAlign:'right', ...corMargem(r.margem_pct) }}>
                        {fmtNum(r.margem_pct)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}

        {/* ══════════ ABA PRODUTOS ══════════ */}
        {aba === 'produtos' && (
          <div>
            <div style={{ display:'flex', gap:6, marginBottom:12 }}>
              <button onClick={() => setSubAbaProd('valoresEstoque')}
                style={subTabBtnStyle(subAbaProd === 'valoresEstoque')}>Valores do Estoque</button>
            </div>

            {subAbaProd === 'valoresEstoque' && (
              <>
                <div className={s.headerRow} style={{ marginBottom:12 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:12.5, cursor:'pointer' }}>
                    <input type="checkbox" checked={filtroValEst.incluir_inativos}
                      onChange={e => setFiltroValEst(f => ({ ...f, incluir_inativos: e.target.checked }))} />
                    Incluir produtos inativos
                  </label>
                  {[
                    ['estoque_positivo', 'Estoque maior que zero'],
                    ['custo_positivo',   'Custo maior que zero'],
                    ['venda_positivo',   'Venda maior que zero'],
                  ].map(([k, label]) => (
                    <label key={k} style={{ display:'flex', alignItems:'center', gap:4, fontSize:12.5, cursor:'pointer' }}>
                      <input type="checkbox" checked={filtroValEst[k]}
                        onChange={e => setFiltroValEst(f => ({ ...f, [k]: e.target.checked }))} />
                      {label}
                    </label>
                  ))}
                  <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                    onClick={carregarValoresEstoque} disabled={loadValEst}>
                    {loadValEst ? 'Carregando...' : 'Gerar'}
                  </button>
                  <BtnExportar disabled={!valEstoque?.itens?.length} onClick={() => setModalExportar({
                    titulo: 'Valores do Estoque', nomeArquivo: 'valores-do-estoque', filtros: filtrosGerados.valEstoque,
                    dados: valEstoque.itens,
                    colunas: [
                      { key: 'codigo_interno', label: 'Código' },
                      { key: 'nome', label: 'Descrição' },
                      { key: 'unidade', label: 'Unidade' },
                      { key: 'estoque', label: 'Qtd Estoque', direita: true, render: r => fmtQtd(r.estoque) },
                      { key: 'preco_custo', label: 'Custo', direita: true, render: r => fmtMoeda(r.preco_custo) },
                      { key: 'preco_venda', label: 'Venda', direita: true, render: r => fmtMoeda(r.preco_venda) },
                      { key: 'total_custo', label: 'Total Custo (Qtd × Custo)', direita: true, render: r => fmtMoeda(r.total_custo) },
                      { key: 'total_venda', label: 'Total Venda (Qtd × Venda)', direita: true, render: r => fmtMoeda(r.total_venda) },
                    ],
                    totais: {
                      total_custo: fmtMoeda(valEstoque.total_custo),
                      total_venda: fmtMoeda(valEstoque.total_venda),
                    },
                  })} />
                </div>

                {!valEstoque && !loadValEst && (
                  <p style={{ color:'#888' }}>Clique em "Gerar". Para imprimir só algumas colunas, use "Exportar/Imprimir" e desmarque as que não quiser.</p>
                )}
                {valEstoque && valEstoque.itens.length === 0 && !loadValEst && (
                  <p style={{ color:'#888' }}>Nenhum produto encontrado.</p>
                )}

                {valEstoque?.itens?.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead style={{ position:'sticky', top:0, zIndex:1 }}>
                      <tr>
                        <th style={{ ...thStyle, textAlign:'left' }}>Código</th>
                        <th style={{ ...thStyle, textAlign:'left' }}>Descrição</th>
                        <th style={thStyle}>Unidade</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Qtd Estoque</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Custo</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Venda</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Total Custo</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Total Venda</th>
                      </tr>
                    </thead>
                    <tbody>
                      {valEstoque.itens.map((r, i) => (
                        <tr key={r.id} style={i % 2 === 0 ? trPar : trImpar}>
                          <td style={tdStyle}>{r.codigo_interno}</td>
                          <td style={tdStyle}>{r.nome}{r.inativo ? ' (inativo)' : ''}</td>
                          <td style={{ ...tdStyle, textAlign:'center' }}>{r.unidade}</td>
                          <td style={{ ...tdStyle, textAlign:'right', color: r.estoque < 0 ? '#dc3545' : undefined }}>{fmtQtd(r.estoque)}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.preco_custo)}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.preco_venda)}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.total_custo)}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.total_venda)}</td>
                        </tr>
                      ))}
                      <tr style={{ background:'#dde3ef', fontWeight:'bold' }}>
                        <td colSpan={6} style={tdStyle}>TOTAL ({valEstoque.itens.length} produto(s))</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(valEstoque.total_custo)}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(valEstoque.total_venda)}</td>
                      </tr>
                    </tbody>
                  </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══════════ ABA AGING ══════════ */}
        {aba === 'aging' && (
          <div>
            {loadAging && <p style={{ color:'#666' }}>Carregando aging...</p>}

            <div style={{ display:'flex', gap:24, flexWrap:'wrap' }}>
              {/* Contas a Pagar */}
              <div style={{ flex:1, minWidth:320 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                  <h3 style={{ margin:0, color:'#0a3a8c', fontSize:14 }}>
                    Contas a Pagar — Em Aberto
                  </h3>
                  <BtnExportar disabled={!agingPagar.length} onClick={() => setModalExportar({
                    titulo: 'Aging — Contas a Pagar', nomeArquivo: 'aging-contas-a-pagar',
                    dados: agingPagar,
                    colunas: [
                      { key: 'faixa', label: 'Faixa' },
                      { key: 'qtd', label: 'Qtd' },
                      { key: 'total', label: 'Total', render: r => fmtMoeda(r.total) },
                    ],
                  })} />
                </div>
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, textAlign:'left' }}>Faixa</th>
                      <th style={{ ...thStyle, textAlign:'right' }}>Qtd</th>
                      <th style={{ ...thStyle, textAlign:'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agingPagar.length === 0 ? (
                      <tr>
                        <td colSpan={3} style={{ ...tdStyle, textAlign:'center', color:'#888', padding:12 }}>
                          Sem contas em aberto
                        </td>
                      </tr>
                    ) : agingPagar.map((r, i) => (
                      <tr key={i} style={corAging(r.faixa)}>
                        <td style={tdStyle}>{r.faixa}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{r.qtd}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.total)}</td>
                      </tr>
                    ))}
                    {agingPagar.length > 0 && (
                      <tr style={{ fontWeight:'bold', background:'#f5f5f5' }}>
                        <td style={tdStyle}>TOTAL</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>
                          {agingPagar.reduce((a,r) => a + Number(r.qtd||0), 0)}
                        </td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>
                          {fmtMoeda(agingPagar.reduce((a,r) => a + Number(r.total||0), 0))}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>

              {/* Contas a Receber */}
              <div style={{ flex:1, minWidth:320 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                  <h3 style={{ margin:0, color:'#0a3a8c', fontSize:14 }}>
                    Contas a Receber — Em Aberto
                  </h3>
                  <BtnExportar disabled={!agingReceber.length} onClick={() => setModalExportar({
                    titulo: 'Aging — Contas a Receber', nomeArquivo: 'aging-contas-a-receber',
                    dados: agingReceber,
                    colunas: [
                      { key: 'faixa', label: 'Faixa' },
                      { key: 'qtd', label: 'Qtd' },
                      { key: 'total', label: 'Total', render: r => fmtMoeda(r.total) },
                    ],
                  })} />
                </div>
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, textAlign:'left' }}>Faixa</th>
                      <th style={{ ...thStyle, textAlign:'right' }}>Qtd</th>
                      <th style={{ ...thStyle, textAlign:'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agingReceber.length === 0 ? (
                      <tr>
                        <td colSpan={3} style={{ ...tdStyle, textAlign:'center', color:'#888', padding:12 }}>
                          Sem contas em aberto
                        </td>
                      </tr>
                    ) : agingReceber.map((r, i) => (
                      <tr key={i} style={corAging(r.faixa)}>
                        <td style={tdStyle}>{r.faixa}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{r.qtd}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.total)}</td>
                      </tr>
                    ))}
                    {agingReceber.length > 0 && (
                      <tr style={{ fontWeight:'bold', background:'#f5f5f5' }}>
                        <td style={tdStyle}>TOTAL</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>
                          {agingReceber.reduce((a,r) => a + Number(r.qtd||0), 0)}
                        </td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>
                          {fmtMoeda(agingReceber.reduce((a,r) => a + Number(r.total||0), 0))}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── COMISSÕES ─── */}
        {aba === 'comissoes' && (
          <div>
            <div style={{ display:'flex', gap:6, marginBottom:12 }}>
              <button
                onClick={() => setSubAbaCom('detalhado')}
                style={subTabBtnStyle(subAbaCom === 'detalhado')}
              >Detalhado</button>
              <button
                onClick={() => setSubAbaCom('resumido')}
                style={subTabBtnStyle(subAbaCom === 'resumido')}
              >Resumido</button>
              <button
                onClick={() => setSubAbaCom('produto')}
                style={subTabBtnStyle(subAbaCom === 'produto')}
              >Detalhado por Produto</button>
            </div>

            <div className={s.headerRow} style={{ marginBottom:12 }}>
              <label className={s.headerLabel}>De:</label>
              <InputDataFiltro className={s.inputErp} style={{ width:130 }}
                value={filtroCom.ini}
                onChange={e => setFiltroCom(f => ({...f, ini: e.target.value}))} />
              <label className={s.headerLabel}>Até:</label>
              <InputDataFiltro className={s.inputErp} style={{ width:130 }}
                value={filtroCom.fim}
                onChange={e => setFiltroCom(f => ({...f, fim: e.target.value}))} />
              {(subAbaCom === 'detalhado' || subAbaCom === 'produto') && (
                <>
                  <label className={s.headerLabel}>Vendedor:</label>
                  <select className={s.selectErp} style={{ minWidth:180 }}
                    value={filtroCom.vendedor_id}
                    onChange={e => setFiltroCom(f => ({...f, vendedor_id: e.target.value}))}>
                    <option value="">— Todos —</option>
                    {vendedores.map(v => <option key={v.id} value={v.id}>{v.nome}</option>)}
                  </select>
                </>
              )}
              <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                onClick={carregarComissoes} disabled={loadComissoes}>
                {loadComissoes ? 'Carregando...' : 'Gerar'}
              </button>
              {subAbaCom === 'detalhado' && (
                <BtnExportar disabled={!comissoes?.linhas?.length} onClick={() => setModalExportar({
                  titulo: 'Comissões — Detalhado', nomeArquivo: 'comissoes-detalhado', filtros: filtrosGerados.comissoes,
                  dados: comissoes?.linhas || [],
                  colunas: [
                    { key: 'venda_id', label: 'Nº Venda' },
                    { key: 'data_base', label: 'Data', render: r => fmtData(r.data_base) },
                    { key: 'cliente_nome', label: 'Cliente' },
                    { key: 'vendedor_nome', label: 'Vendedor' },
                    { key: 'total_venda', label: 'Total Venda', render: r => fmtMoeda(r.total_venda) },
                    { key: 'pct', label: '%', render: r => fmtNum(r.pct) + '%' },
                    { key: 'comissao', label: 'Comissão', render: r => fmtMoeda(r.comissao) },
                  ],
                })} />
              )}
              {subAbaCom === 'resumido' && (
                <BtnExportar disabled={!comissoes?.por_vendedor?.length} onClick={() => setModalExportar({
                  titulo: 'Comissões — Resumido', nomeArquivo: 'comissoes-resumido', filtros: filtrosGerados.comissoes,
                  dados: comissoes?.por_vendedor || [],
                  colunas: [
                    { key: 'vendedor_id', label: 'Cód. Vendedor' },
                    { key: 'vendedor_nome', label: 'Nome Vendedor' },
                    { key: 'qtd_pedidos', label: 'Qtd Pedidos' },
                    { key: 'valor_liquido', label: 'Valor Líquido', render: r => fmtMoeda(r.valor_liquido) },
                    { key: 'percentual', label: '%', render: r => fmtNum(r.percentual) + '%' },
                    { key: 'valor_comissao', label: 'Comissão', render: r => fmtMoeda(r.valor_comissao) },
                  ],
                })} />
              )}
              {subAbaCom === 'produto' && (
                <BtnExportar disabled={!comissoes?.por_produto?.length} onClick={() => setModalExportar({
                  titulo: 'Comissões — Detalhado por Produto', nomeArquivo: 'comissoes-detalhado-por-produto', filtros: filtrosGerados.comissoes,
                  dados: comissoes?.por_produto || [],
                  colunas: [
                    { key: 'venda_id', label: 'Nº Venda' },
                    { key: 'data_base', label: 'Data', render: r => fmtData(r.data_base) },
                    { key: 'cliente_nome', label: 'Cliente' },
                    { key: 'vendedor_nome', label: 'Vendedor' },
                    { key: 'produto_nome', label: 'Produto' },
                    { key: 'quantidade', label: 'Qtd', render: r => fmtNum(r.quantidade) },
                    { key: 'total_item', label: 'Total Item', render: r => fmtMoeda(r.total_item) },
                    { key: 'pct', label: '%', render: r => fmtNum(r.pct) + '%' },
                    { key: 'comissao', label: 'Comissão', render: r => fmtMoeda(r.comissao) },
                  ],
                })} />
              )}
            </div>

            {!comissoes && !loadComissoes && (
              <p style={{ color:'#888' }}>Selecione o período (e o vendedor, se quiser) e clique em "Gerar".</p>
            )}

            {comissoes && (
              <>
                <p style={{ fontSize:12, color:'#555', margin:'0 0 8px' }}>
                  Base: <strong>{comissoes.base_pagamento === 'baixa_cr'
                    ? 'Recebimento (baixa do contas a receber — proporcional à parcela; vendas à vista na data da venda)'
                    : 'Emissão da venda'}</strong>
                  {' · '}Percentual: <strong>{comissoes.base_percentual === 'vendedor'
                    ? 'por vendedor (cadastro do usuário)' : 'por produto'}</strong>
                  {' '}<span style={{ color:'#888' }}>(o % é o gravado em cada venda)</span>
                </p>

                {subAbaCom === 'detalhado' && (
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Nº Venda</th>
                      <th style={thStyle}>Data</th>
                      <th style={{ ...thStyle, textAlign:'left' }}>Cliente</th>
                      <th style={{ ...thStyle, textAlign:'left' }}>Vendedor</th>
                      <th style={{ ...thStyle, textAlign:'right' }}>Total Venda</th>
                      <th style={{ ...thStyle, textAlign:'right' }}>%</th>
                      <th style={{ ...thStyle, textAlign:'right' }}>Comissão</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comissoes.linhas.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ ...tdStyle, textAlign:'center', color:'#888', padding:12 }}>
                          Nenhuma comissão no período{comissoes.base_pagamento === 'baixa_cr'
                            ? ' (base recebimento: só entram parcelas baixadas e vendas à vista)' : ''}.
                        </td>
                      </tr>
                    ) : comissoes.linhas.map((l, i) => (
                      <tr key={i}>
                        <td style={{ ...tdStyle, textAlign:'center' }}>{l.venda_id}</td>
                        <td style={{ ...tdStyle, textAlign:'center' }}>
                          {new Date(String(l.data_base).slice(0,10) + 'T12:00:00').toLocaleDateString('pt-BR')}
                        </td>
                        <td style={tdStyle}>{l.cliente_nome}</td>
                        <td style={tdStyle}>{l.vendedor_nome || '—'}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(l.total_venda)}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(l.pct)}%</td>
                        <td style={{ ...tdStyle, textAlign:'right', fontWeight:600 }}>{fmtMoeda(l.comissao)}</td>
                      </tr>
                    ))}
                    {comissoes.linhas.length > 0 && (
                      <tr style={{ fontWeight:'bold', background:'#f5f5f5' }}>
                        <td colSpan={4} style={tdStyle}>TOTAL ({comissoes.linhas.length} lançamento(s))</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(comissoes.total_vendas)}</td>
                        <td style={tdStyle}></td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(comissoes.total_comissao)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
                )}

                {subAbaCom === 'resumido' && (
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Cód. Vendedor</th>
                      <th style={{ ...thStyle, textAlign:'left' }}>Nome Vendedor</th>
                      <th style={{ ...thStyle, textAlign:'right' }}>Qtd Pedidos</th>
                      <th style={{ ...thStyle, textAlign:'right' }}>Valor Líquido</th>
                      <th style={{ ...thStyle, textAlign:'right' }}>%</th>
                      <th style={{ ...thStyle, textAlign:'right' }}>Comissão</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(comissoes.por_vendedor || []).length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ ...tdStyle, textAlign:'center', color:'#888', padding:12 }}>
                          Nenhuma comissão no período.
                        </td>
                      </tr>
                    ) : comissoes.por_vendedor.map((v, i) => (
                      <tr key={i}>
                        <td style={{ ...tdStyle, textAlign:'center' }}>{v.vendedor_id ?? '—'}</td>
                        <td style={tdStyle}>{v.vendedor_nome}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{v.qtd_pedidos}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(v.valor_liquido)}</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(v.percentual)}%</td>
                        <td style={{ ...tdStyle, textAlign:'right', fontWeight:600 }}>{fmtMoeda(v.valor_comissao)}</td>
                      </tr>
                    ))}
                    {(comissoes.por_vendedor || []).length > 0 && (
                      <tr style={{ fontWeight:'bold', background:'#f5f5f5' }}>
                        <td colSpan={2} style={tdStyle}>TOTAL ({comissoes.por_vendedor.length} vendedor(es))</td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>
                          {comissoes.por_vendedor.reduce((a, v) => a + Number(v.qtd_pedidos||0), 0)}
                        </td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(comissoes.total_vendas)}</td>
                        <td style={tdStyle}></td>
                        <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(comissoes.total_comissao)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
                )}

                {subAbaCom === 'produto' && (
                <div style={{ overflowX: 'auto' }}>
                {comissoes.base_pagamento === 'baixa_cr' ? (
                  <p style={{ color:'#888' }}>
                    Detalhado por produto não está disponível no modo "Recebimento" (a comissão de
                    cada parcela é proporcional ao valor recebido, não dá pra ratear de volta por
                    item com segurança). Troque a base de "Rotina: Pagamento de Comissão" pra
                    "Emissão da venda" em Organização → Parâmetros, se precisar deste relatório.
                  </p>
                ) : (
                  <>
                  {comissoes.base_percentual !== 'produto' && (
                    <p style={{ color:'#a15c00', fontSize:12, marginBottom:8 }}>
                      ⚠ Esta organização calcula comissão <strong>por vendedor</strong>, não por
                      produto — o % abaixo é o mesmo (fixo do vendedor) em toda linha, só a
                      quebra por produto muda.
                    </p>
                  )}
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Nº Venda</th>
                        <th style={thStyle}>Data</th>
                        <th style={{ ...thStyle, textAlign:'left' }}>Cliente</th>
                        <th style={{ ...thStyle, textAlign:'left' }}>Vendedor</th>
                        <th style={{ ...thStyle, textAlign:'left' }}>Produto</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Qtd</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Total Item</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>%</th>
                        <th style={{ ...thStyle, textAlign:'right' }}>Comissão</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(comissoes.por_produto || []).length === 0 ? (
                        <tr>
                          <td colSpan={9} style={{ ...tdStyle, textAlign:'center', color:'#888', padding:12 }}>
                            Nenhuma comissão no período.
                          </td>
                        </tr>
                      ) : comissoes.por_produto.map((l, i) => (
                        <tr key={i}>
                          <td style={{ ...tdStyle, textAlign:'center' }}>{l.venda_id}</td>
                          <td style={{ ...tdStyle, textAlign:'center' }}>
                            {new Date(String(l.data_base).slice(0,10) + 'T12:00:00').toLocaleDateString('pt-BR')}
                          </td>
                          <td style={tdStyle}>{l.cliente_nome}</td>
                          <td style={tdStyle}>{l.vendedor_nome || '—'}</td>
                          <td style={tdStyle}>{l.produto_nome || '—'}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(l.quantidade)}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(l.total_item)}</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>{fmtNum(l.pct)}%</td>
                          <td style={{ ...tdStyle, textAlign:'right', fontWeight:600 }}>{fmtMoeda(l.comissao)}</td>
                        </tr>
                      ))}
                      {(comissoes.por_produto || []).length > 0 && (
                        <tr style={{ fontWeight:'bold', background:'#f5f5f5' }}>
                          <td colSpan={6} style={tdStyle}>TOTAL ({comissoes.por_produto.length} item(ns))</td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>
                            {fmtMoeda(comissoes.por_produto.reduce((a, l) => a + Number(l.total_item || 0), 0))}
                          </td>
                          <td style={tdStyle}></td>
                          <td style={{ ...tdStyle, textAlign:'right' }}>
                            {fmtMoeda(comissoes.por_produto.reduce((a, l) => a + Number(l.comissao || 0), 0))}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  </>
                )}
                </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ─── CONTAS A PAGAR ─── */}
        {aba === 'contasPagar' && (
          <div>
            <div style={{ display:'flex', gap:6, marginBottom:12 }}>
              <button style={subTabBtnStyle(true)}>Por Fornecedor</button>
            </div>

            <div className={s.headerRow} style={{ marginBottom:12 }}>
              <label className={s.headerLabel}>Vencimento de:</label>
              <InputDataFiltro className={s.inputErp} style={{ width:130 }}
                value={filtroCP.ini}
                onChange={e => setFiltroCP(f => ({...f, ini: e.target.value}))} />
              <label className={s.headerLabel}>até:</label>
              <InputDataFiltro className={s.inputErp} style={{ width:130 }}
                value={filtroCP.fim}
                onChange={e => setFiltroCP(f => ({...f, fim: e.target.value}))} />
              <label className={s.headerLabel}>Fornecedor:</label>
              <input className={s.inputErp} style={{ width:220 }}
                placeholder="Nome ou código — vazio = todos"
                value={filtroCP.fornecedor}
                onChange={e => setFiltroCP(f => ({...f, fornecedor: e.target.value}))} />
              <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                onClick={carregarContasPagar} disabled={loadCP}>
                {loadCP ? 'Carregando...' : 'Gerar'}
              </button>
              <BtnExportar disabled={!contasPagarLista.length} onClick={() => setModalExportar({
                titulo: 'Contas a Pagar por Fornecedor', nomeArquivo: 'contas-a-pagar', filtros: filtrosGerados.contasPagar,
                dados: contasPagarLista,
                colunas: [
                  { key: 'numero_documento', label: 'Documento', render: r => r.numero_documento || `#${r.id}` },
                  { key: 'fornecedor_nome', label: 'Fornecedor' },
                  { key: 'complemento', label: 'Complemento' },
                  { key: 'data_emissao', label: 'Emissão', render: r => fmtData(r.data_emissao) },
                  { key: 'data_vencimento', label: 'Vencimento', render: r => fmtData(r.data_vencimento) },
                  { key: 'forma_pagamento', label: 'Forma Pagamento' },
                  { key: 'valor', label: 'Valor Documento', render: r => fmtMoeda(r.valor) },
                ],
              })} />
            </div>

            {contasPagarLista.length === 0 && !loadCP && (
              <p style={{ color:'#888' }}>Selecione o período (e o fornecedor, se quiser) e clique em "Gerar".</p>
            )}

            {contasPagarLista.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Documento</th>
                    <th style={{ ...thStyle, textAlign:'left' }}>Fornecedor</th>
                    <th style={{ ...thStyle, textAlign:'left' }}>Complemento</th>
                    <th style={thStyle}>Emissão</th>
                    <th style={thStyle}>Vencimento</th>
                    <th style={{ ...thStyle, textAlign:'left' }}>Forma Pagamento</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Valor Documento</th>
                  </tr>
                </thead>
                <tbody>
                  {contasPagarLista.map((r, i) => (
                    <tr key={i} style={i % 2 === 0 ? trPar : trImpar}>
                      <td style={tdStyle}>{r.numero_documento || `#${r.id}`}</td>
                      <td style={tdStyle}>{r.fornecedor_nome || '—'}</td>
                      <td style={tdStyle}>{r.complemento || '—'}</td>
                      <td style={{ ...tdStyle, textAlign:'center' }}>{fmtData(r.data_emissao)}</td>
                      <td style={{ ...tdStyle, textAlign:'center' }}>{fmtData(r.data_vencimento)}</td>
                      <td style={tdStyle}>{r.forma_pagamento || '—'}</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.valor)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background:'#e8f0ff', fontWeight:'bold' }}>
                    <td colSpan={6} style={tdStyle}>TOTAL ({contasPagarLista.length} documento(s))</td>
                    <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(totalContasPagar)}</td>
                  </tr>
                </tfoot>
              </table>
              </div>
            )}
          </div>
        )}

        {/* ─── CONTAS A RECEBER ─── */}
        {aba === 'contasReceber' && (
          <div>
            <div style={{ display:'flex', gap:6, marginBottom:12 }}>
              <button style={subTabBtnStyle(true)}>Por Período</button>
            </div>

            <div className={s.headerRow} style={{ marginBottom:12 }}>
              <label className={s.headerLabel}>Vencimento de:</label>
              <InputDataFiltro className={s.inputErp} style={{ width:130 }}
                value={filtroCR.ini}
                onChange={e => setFiltroCR(f => ({...f, ini: e.target.value}))} />
              <label className={s.headerLabel}>até:</label>
              <InputDataFiltro className={s.inputErp} style={{ width:130 }}
                value={filtroCR.fim}
                onChange={e => setFiltroCR(f => ({...f, fim: e.target.value}))} />
              <label className={s.headerLabel}>Cliente:</label>
              <input className={s.inputErp} style={{ width:220 }}
                placeholder="Nome ou código — vazio = todos"
                value={filtroCR.cliente}
                onChange={e => setFiltroCR(f => ({...f, cliente: e.target.value}))} />
              <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12.5, cursor:'pointer' }}
                title="Cartão pendente é baixado na tela Baixa de Cartões, não aqui — por isso fica de fora por padrão">
                <input type="checkbox" checked={mostrarCartoesCR}
                  onChange={e => setMostrarCartoesCR(e.target.checked)} />
                Mostrar cartões
              </label>
              <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                onClick={carregarContasReceber} disabled={loadCR}>
                {loadCR ? 'Carregando...' : 'Gerar'}
              </button>
              <BtnExportar disabled={!contasReceberLista.length} onClick={() => setModalExportar({
                titulo: 'Contas a Receber por Período', nomeArquivo: 'contas-a-receber', filtros: filtrosGerados.contasReceber,
                dados: contasReceberLista,
                colunas: [
                  { key: 'numero_documento', label: 'Documento', render: r => r.numero_documento || `#${r.id}` },
                  { key: 'cliente_nome', label: 'Cliente' },
                  { key: 'complemento', label: 'Complemento' },
                  { key: 'data_emissao', label: 'Emissão', render: r => fmtData(r.data_emissao) },
                  { key: 'data_vencimento', label: 'Vencimento', render: r => fmtData(r.data_vencimento) },
                  { key: 'forma_recebimento', label: 'Forma Pagamento' },
                  { key: 'valor', label: 'Valor', render: r => fmtMoeda(r.valor) },
                ],
              })} />
            </div>
            <p style={{ fontSize:11.5, color:'#888', margin:'0 0 12px' }}>
              * Só o que ainda está em aberto (crediário, boleto...) — vendas já liquidadas na hora
              (dinheiro, PIX) não aparecem aqui, e cartão pendente só entra marcando "Mostrar cartões".
            </p>

            {contasReceberLista.length === 0 && !loadCR && (
              <p style={{ color:'#888' }}>Selecione o período (e o cliente, se quiser) e clique em "Gerar".</p>
            )}

            {contasReceberLista.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Documento</th>
                    <th style={{ ...thStyle, textAlign:'left' }}>Cliente</th>
                    <th style={{ ...thStyle, textAlign:'left' }}>Complemento</th>
                    <th style={thStyle}>Emissão</th>
                    <th style={thStyle}>Vencimento</th>
                    <th style={{ ...thStyle, textAlign:'left' }}>Forma Pagamento</th>
                    <th style={{ ...thStyle, textAlign:'right' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {contasReceberLista.map((r, i) => (
                    <tr key={i} style={i % 2 === 0 ? trPar : trImpar}>
                      <td style={tdStyle}>{r.numero_documento || `#${r.id}`}</td>
                      <td style={tdStyle}>{r.cliente_nome || '—'}</td>
                      <td style={tdStyle}>{r.complemento || '—'}</td>
                      <td style={{ ...tdStyle, textAlign:'center' }}>{fmtData(r.data_emissao)}</td>
                      <td style={{ ...tdStyle, textAlign:'center' }}>{fmtData(r.data_vencimento)}</td>
                      <td style={tdStyle}>{r.forma_recebimento || '—'}</td>
                      <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(r.valor)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background:'#e8f0ff', fontWeight:'bold' }}>
                    <td colSpan={6} style={tdStyle}>TOTAL ({contasReceberLista.length} documento(s))</td>
                    <td style={{ ...tdStyle, textAlign:'right' }}>{fmtMoeda(totalContasReceber)}</td>
                  </tr>
                </tfoot>
              </table>
              </div>
            )}
          </div>
        )}

      </div>

      {modalExportar && (
        <ModalExportarLista
          {...modalExportar}
          onClose={() => setModalExportar(null)}
        />
      )}
    </div>
  );
}
